/* eslint-disable @typescript-eslint/no-explicit-any */
import { useNotification } from '@/containers'
import { api } from '@/utils'
import { translateSchema } from '@/utils/validations'
import clsx from 'clsx'
import { useFormik } from 'formik'
import { useTranslations } from 'next-intl'
import { useRouter } from 'next/router'
import type { Dispatch, SetStateAction } from 'react'
import { useEffect, useRef, useState } from 'react'
import StarRatings from 'react-star-ratings'
import { toast } from 'react-toastify'
import { FormButton, FormTextArea, FormTextRequire, FormView } from '../Form'
import { PopupClose } from '../Icon/PopupClose'
import ImageOptimize from '../ImageOptimize'
import PopupBody from './PopupBody'
import PopupFrame from './PopupFrame'
import PopupTitle from './PopupTitle'

interface ModalCreateReview {
  showModalCreateReview: boolean
  setShowModalCreateReview: Dispatch<SetStateAction<boolean>>
  productName: string
  productId: string
}

interface UploadResponse {
  index: number
  url: string
}
const ModalCreateReview = ({
  showModalCreateReview,
  productName,
  productId,
  setShowModalCreateReview,
}: ModalCreateReview) => {
  const { systemConfig } = useNotification()
  const [listImageReview, setListImageReview] = useState<UploadResponse[]>([])
  const [isSubmitted, setIsSubmitted] = useState(false)
  const [isProcessing, setIsProcessing] = useState(false)
  const [error, setError] = useState({ active: false, message: '' })
  const [pointRate, setPointRate] = useState(0)
  const tReview = useTranslations('Review')
  const tCommon = useTranslations('Common')
  const router = useRouter()
  const changeRate = (e: number) => {
    if (e && e > 0) {
      setPointRate(e)
      formik.handleChange({
        target: {
          value: e,
          name: 'point',
        },
      })
    }
  }

  const onSubmitFn = () => {
    setIsSubmitted(true)
  }

  useEffect(() => {
    if (error && error.active) {
      toast.error(error.message, {
        position: 'bottom-center',
        autoClose: 300,
        hideProgressBar: true,
        closeOnClick: true,
        pauseOnHover: false,
        draggable: true,
        progress: undefined,
        theme: 'dark',
      })
    }
  }, [error])
  const onInputChange = async (
    e: React.ChangeEvent<HTMLInputElement>
  ): Promise<void> => {
    if (!e.target.files || e.target.files.length === 0) {
      console.error('Select a file')
      return
    }

    const files = e.target.files
    setIsProcessing(true)
    try {
      const uploadPromises = Array.from(files).map(async (file, index) => {
        const fileSize = file.size
        const maxSize = 3 * 1024 * 1024 // 3MB

        if (fileSize > maxSize) {
          if (!error.active) {
            setError({
              message: 'Kích thước mỗi ảnh không được vượt quá 3MB.',
              active: true,
            })
          }
          return null
        }
        if (
          files.length > 5 ||
          (listImageReview && listImageReview.length + files.length > 5)
        ) {
          if (!error.active) {
            setError({
              message: 'Bạn chỉ được phép chọn tối đa 5 ảnh.',
              active: true,
            })
          }
          return null
        }
        const formData = new FormData()
        formData.append('files', file, file.name)

        const res = await api(`file/upload`, {
          method: 'post',
          baseURL: process.env.NEXT_PUBLIC_STORAGE_API_URL,
          data: formData,
        })

        if (res instanceof Error) {
          setIsProcessing(false)
          throw new Error('Có lỗi khi tải ảnh')
        }

        return { index, url: res.data[0].fullPath }
      })

      const uploadedFiles = await Promise.all(uploadPromises)
      const filteredUploadedFiles = uploadedFiles.filter(
        file => file !== null
      ) as any
      setListImageReview(prevList =>
        prevList.concat(filteredUploadedFiles as any)
      )
    } catch (error) {
      setIsProcessing(false)
    } finally {
      setIsProcessing(false)
    }
  }
  const formik = useFormik({
    initialValues: {
      productId: '',
      point: '',
      content: '',
      images: [],
    },

    enableReinitialize: true,
    validationSchema: translateSchema('reviewProduct', tReview),

    onSubmit: async values => {
      setIsProcessing(true)
      const data = {
        point: pointRate,
        images: listImageReview?.map(item => item.url),
        productId: productId,
        content: values.content,
      }

      const res = await api(`api/review`, {
        method: 'POST',
        baseURL: process.env.NEXT_PUBLIC_CATALOG_API_URL,
        data: data,
      })

      if (res instanceof Error) {
        setIsProcessing(false)
        toast.error(res.message, {
          position: 'top-center',
          autoClose: 3000,
          hideProgressBar: true,
          closeOnClick: true,
          pauseOnHover: false,
          draggable: true,
          progress: undefined,
          theme: 'light',
        })
        return
      }

      toast.success(tReview('success'), {
        position: 'top-center',
        autoClose: 3000,
        hideProgressBar: true,
        closeOnClick: true,
        pauseOnHover: false,
        draggable: true,
        progress: undefined,
        theme: 'light',
      })
      setShowModalCreateReview(false)
      router.reload()
    },
  })

  const inputRef = useRef<HTMLInputElement>(null)
  return (
    <PopupFrame
      open={showModalCreateReview}
      onClose={() => {
        setShowModalCreateReview(false)
      }}
      closeText={tCommon('close')}
      type='voucher'
      className='min-h-[480px] w-full max-w-[560px] bg-white'
    >
      <PopupTitle
        className='border-b border-zinc-300 px-3 pt-3'
        type={'voucher'}
      >
        {
          <div className='relative flex w-full flex-wrap items-center'>
            <p className='w-[70%] text-lg font-bold'>Viết đánh giá</p>
            <div
              onClick={() => setShowModalCreateReview(false)}
              className='absolute right-0 top-[50%] z-10 flex translate-y-[-50%] cursor-pointer items-center gap-1'
            >
              <PopupClose className=' text-dark-400' />
              {tCommon('close')}
            </div>
          </div>
        }
      </PopupTitle>
      <PopupBody>
        <div className='listCoupons mt-[18px] flex w-full flex-wrap justify-between gap-y-[12px] border-dark-100 scrollbar-hide'>
          <FormView className={''} onSubmit={formik.handleSubmit}>
            <div className='relative mt-3 flex w-full flex-wrap justify-center px-3'>
              <div className='text-black mb-2 w-full whitespace-break-spaces text-center text-lg font-bold'>
                {productName}
              </div>
              <StarRatings
                starRatedColor='#FFCD4B'
                starHoverColor='#FFCD4B'
                numberOfStars={5}
                rating={pointRate}
                name='point'
                changeRating={changeRate}
                starDimension='36px'
                starSpacing='2px'
                svgIconPath='M20 0L26.1803 13.1672L40 15.2786L30 25.5279L32.3607 40L20 33.1672L7.63932 40L10 25.5279L0 15.2786L13.8197 13.1672L20 0Z'
                svgIconViewBox='0 0 40 40'
              />
              <div className='absolute bottom-[-28px] left-[50%] flex w-full translate-x-[-50%] justify-center'>
                {isSubmitted && formik.errors.point ? (
                  <FormTextRequire className=' text-fieldInput text-sm font-normal text-primary-second'>
                    {formik.errors.point as string}
                  </FormTextRequire>
                ) : (
                  ''
                )}
              </div>
            </div>
            <div className='main my-4 h-full w-full overflow-y-auto px-[20px]'>
              <div className='text-base font-bold leading-tight text-dark-800'>
                Cảm nhận về sản phẩm và dịch vụ của{' '}
                {systemConfig?.websiteBrandName}
              </div>
              <FormTextArea
                value={formik.values.content}
                isError={isSubmitted && formik.errors.content}
                onChange={e => {
                  formik.handleChange({
                    target: {
                      value: e.target.value,
                      name: 'content',
                    },
                  })
                }}
                alert
                className={clsx(
                  isSubmitted &&
                    formik.errors.content &&
                    'border border-primary-second',
                  'mt-2.5 rounded-lg border border-solid border-dark-200 text-left transition placeholder:text-dark-400/70 hover:cursor-pointer hover:border-blue focus:border-blue'
                )}
                name='content'
                id='reviewAndRate'
                // cols={30}
                // className='mt-2.5 w-full rounded-sm border border-neutral-300 p-3 text-base font-normal leading-tight text-dark-800 placeholder:opacity-30'
                rows={3}
                placeholder='Nhập cảm nhận'
              />
              <div className='mt-2'>
                <p className='mb-1 text-base font-bold leading-tight text-dark-800'>
                  Hình ảnh
                </p>
                <ul className='mb-1 flex gap-2'>
                  {listImageReview &&
                    listImageReview.length > 0 &&
                    listImageReview.map((item, index) => {
                      return (
                        <li key={index}>
                          <ImageOptimize
                            src={(item as any).url || ''}
                            alt='review'
                            width={300}
                            height={300}
                            className='aspect-square h-20 w-20 rounded-sm object-cover'
                          />
                        </li>
                      )
                    })}
                  {listImageReview && listImageReview?.length <= 5 && (
                    <li className='aspect-square rounded border border-dashed border-zinc-300 bg-neutral-50'>
                      <div className='uploadBox z-[999] cursor-pointer flex-wrap items-center justify-center gap-0'>
                        <input
                          type='file'
                          ref={inputRef}
                          accept='image/png, image/gif, image/jpeg'
                          onChange={onInputChange}
                          multiple
                          name='uploadAvt'
                          id='uploadAvt'
                          // disabled={loading}
                          className='uploadAvatar invisible absolute left-48 z-10 h-0'
                        />
                        <label
                          htmlFor='uploadAvt'
                          className='flex h-20 w-20 cursor-pointer flex-col flex-wrap content-center items-center justify-center gap-1'
                        >
                          <span className='relative h-3.5 w-3.5'>
                            <svg
                              xmlns='http://www.w3.org/2000/svg'
                              width={14}
                              height={15}
                              viewBox='0 0 14 15'
                              fill='none'
                            >
                              <path
                                d='M7.46863 1.87573H6.53113C6.44779 1.87573 6.40613 1.9174 6.40613 2.00073V6.90698H1.75024C1.66691 6.90698 1.62524 6.94865 1.62524 7.03198V7.96948C1.62524 8.05282 1.66691 8.09448 1.75024 8.09448H6.40613V13.0007C6.40613 13.0841 6.44779 13.1257 6.53113 13.1257H7.46863C7.55196 13.1257 7.59363 13.0841 7.59363 13.0007V8.09448H12.2502C12.3336 8.09448 12.3752 8.05282 12.3752 7.96948V7.03198C12.3752 6.94865 12.3336 6.90698 12.2502 6.90698H7.59363V2.00073C7.59363 1.9174 7.55196 1.87573 7.46863 1.87573Z'
                                fill='black'
                                fillOpacity='0.85'
                              />
                            </svg>
                          </span>
                          <span className='text-black text-center text-sm font-normal leading-snug text-opacity-40'>
                            Upload
                          </span>
                        </label>
                      </div>
                    </li>
                  )}
                </ul>
              </div>
            </div>
            <div className='actionForm flex w-full items-center justify-center px-3'>
              <FormButton
                className={clsx(
                  formik.dirty &&
                    '!bg-primary-base text-fieldInput !text-white',
                  'disabled:text-[rgb(0 0 0 / 25%)] mb-4 h-[48px] w-full rounded bg-dark-200 p-[10px] text-fieldInput font-bold text-dark-800/60 outline-none'
                )}
                // type={'button'}
                disabled={isProcessing}
                onClick={onSubmitFn}
              >
                Gửi đánh giá
              </FormButton>
            </div>
          </FormView>
        </div>
      </PopupBody>
    </PopupFrame>
  )
}

export default ModalCreateReview
